/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Grid } from "@material-ui/core";
import { isEmptyObject } from "../../../utility/helpers";
import FormTextField from "../../../components/FormTextField/FormTextField";
import { Autocomplete } from "@material-ui/lab";
import { PROMPTS_EDIT_TOOLTIP } from "../../../utility/toolTipMessages";
import { ActionButtons, ProgressOverlay } from "../../../components/Common";
import { getPromptTypeSelectArr } from "../utility/lookupHelper";
import { getNameSpaceAndPromptId, getPromptIvrId, isPacePrompt } from "./utils";
import { toSentenceCase } from "../../../utility";
import { isValidPrompt, isValidPromptId } from "../utility/validationHelper";
import { PROMPT_MESSAGE_MAX_LENGTH } from "../../../utility/constants";
import { addPrompt, updatePrompt } from "../services/promptServices";

const { PROMPT_ID_FIELD, PROMPT_FIELD } = PROMPTS_EDIT_TOOLTIP;

const promptTypeSelectArr = getPromptTypeSelectArr();

const validatePrompt = (editedPrompt) => {
	const { promptId, prompt, type } = editedPrompt;
	const errors = {};
	let isValid = true;
	if (!type) {
		errors.type = "Type is required";
		isValid = false;
	}

	if (!promptId) {
		errors.promptId = "ID field is required!";
		isValid = false;
	} else if (!isValidPromptId(promptId)) {
		errors.promptId = "Invalid ID entered!";
		isValid = false;
	}

	if (!prompt) {
		errors.prompt = "Prompt is required";
		isValid = false;
	} else if (prompt.length > PROMPT_MESSAGE_MAX_LENGTH) {
		errors.prompt = `Prompt message exceeds max allowed length ${PROMPT_MESSAGE_MAX_LENGTH}.`;
		isValid = false;
	}

	return { errors, isValid };
};

const getPromptFieldLabel = (promptType) => {
	const match = promptTypeSelectArr.find((x) => x.value === promptType);
	return match ? match.label : "Prompt";
};

/**
 * Modal form used for adding and editing contacts
 * @param {Object} props
 * @param {Boolean} props.isOpen used to control open/closed state of the modal
 */
const PromptEditForm = ({
	allPrompts,
	activePrompt,
	connectPromptsList,
	showMessage,
	onPromptClose,
	onEditComplete,
	onCreateComplete,
	isOpen = true,
}) => {
	const [prompt, setPrompt] = useState({});
	const [validation, setValidation] = useState({ errors: {}, isValid: true });
	const [isEdited, setIsEdited] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const isNew = !activePrompt?.promptId;

	const checkValidPrompt = async () => {
		const { type, prompt: promptMessage } = prompt;
		const errors = {};
		let isValid = true;
		try {
			const validPrompt = await isValidPrompt(type, promptMessage);
			if (!validPrompt) {
				errors.prompt = `Invalid prompt for ${type} type!`;
				isValid = false;
			}
		} catch (error) {
			isValid = false;
		}
		return { errors, isValid };
	};

	const checkDuplicatePrompt = () => {
		const { language } = prompt;
		const errors = {};
		let isValid = true;
		const newPromptId = getNameSpaceAndPromptId(prompt);

		// Check to see if duplicate keys are entered
		const promptExist = allPrompts.find((item) => {
			let itemPromptId;
			if (item.namespace === "") {
				itemPromptId = item.promptId;
			} else {
				itemPromptId = getPromptIvrId(item, true);
			}
			return item.language === language && itemPromptId === newPromptId;
		});
		if (promptExist) {
			errors.promptId = `Error adding duplicate key: ${getPromptIvrId(prompt)}!`;
			isValid = false;
		}
		return { errors, isValid };
	};

	const updateProperty = (prop, value) => {
		setPrompt({ ...prompt, [prop]: value });
	};

	const getError = (prop) => {
		if (!isEdited) {
			return "";
		}
		return validation.errors[prop];
	};

	const onUpdatePrompt = async () => {
		setIsSaving(true);
		const validationResp = await checkValidPrompt();
		setValidation(validationResp);
		if (validationResp.isValid) {
			const newPromptId = getNameSpaceAndPromptId(prompt);
			const response = await updatePrompt(prompt.language, newPromptId, prompt.prompt, prompt.type);
			if (response.status === "success") {
				showMessage("success", `Successfully saved ${getPromptIvrId(prompt)}.`);
				onEditComplete(prompt);
			} else {
				showMessage("error", `Failed to save ${getPromptIvrId(prompt)}.`);
			}
		}
		setIsSaving(false);
	};

	const onCreatePrompt = async () => {
		setIsSaving(true);
		const validationResp = await checkValidPrompt();
		setValidation(validationResp);
		if (validationResp.isValid) {
			const duplicateResp = checkDuplicatePrompt();
			setValidation(duplicateResp);
			if (duplicateResp.isValid) {
				const newPromptId = getNameSpaceAndPromptId(prompt);
				const response = await addPrompt(prompt.language, newPromptId, prompt.prompt, prompt.type);
				if (response.status === "success") {
					const promptsTemp = [...allPrompts];
					promptsTemp.push(prompt);
					showMessage("success", `Successfully created ${getPromptIvrId(prompt)}.`);
					onCreateComplete(prompt);
				} else {
					let msg = "";
					if (response?.result?.code === "ConditionalCheckFailedException") {
						msg = `Error adding duplicate key: ${getPromptIvrId(prompt)}.`;
					} else {
						msg = `Failed to create ${getPromptIvrId(prompt)} prompt!.`;
					}
					showMessage("error", msg);
				}
			}
		}
		setIsSaving(false);
	};

	// Updating validation errors on user's every input.
	useEffect(() => {
		if (!isEmptyObject(prompt)) {
			const validationResp = validatePrompt(prompt);
			setValidation(validationResp);
		}
	}, [prompt, isEdited]);

	useEffect(() => {
		setPrompt({ ...activePrompt });
	}, [activePrompt]);

	return (
		<div>
			{isSaving && <ProgressOverlay />}
			{prompt && (
				<Dialog open={isOpen} onClose={onPromptClose} fullWidth>
					<DialogTitle>{isNew ? "Create new prompt" : "Edit prompt"}</DialogTitle>
					<DialogContent>
						<Grid container justifyContent="center" alignItems="center" direction="column">
							<Grid item className="w-100 d-inline">
								<TextField
									className="w-50 pr-1"
									id="languageTextField"
									label="Language"
									disabled
									defaultValue={prompt.language}
								/>
								<TextField
									className="w-50 pl-1"
									id="namespaceTextField"
									label="Namespace"
									defaultValue={prompt.namespace}
									disabled
								/>
							</Grid>
							<Grid item className="w-100 mt-3">
								<FormTextField
									autoFocus
									label="ID"
									formEdited={isEdited}
									disabled={!isNew}
									setEdited={setIsEdited}
									errorMessage={getError("promptId")}
									startingValue={prompt.promptId || ""}
									setConfigValue={updateProperty}
									valueName={"promptId"}
									styleObject={{ width: "100%" }}
									enableDebounce
									errorCheck={() => !!getError("promptId")}
									toolTipMessage={PROMPT_ID_FIELD}
								/>
							</Grid>
							<Grid item className="w-100 mt-3">
								<TextField
									id="typeTextField"
									required
									disabled={isPacePrompt(prompt)}
									select
									label="Type"
									value={prompt.type}
									size="small"
									margin="dense"
									fullWidth
									error={!!getError("type")}
									onChange={(e) => {
										setPrompt((current) => ({ ...current, type: e.target.value, prompt: "" }));
									}}
									SelectProps={{
										native: true,
									}}
								>
									{promptTypeSelectArr.map((option) => (
										<option key={option.value} value={option.value}>
											{toSentenceCase(option.value)}
										</option>
									))}
								</TextField>
							</Grid>
							<Grid item className="w-100 mt-3">
								{prompt.type === "audio" ? (
									<Autocomplete
										options={connectPromptsList}
										onChange={(_, value) => {
											setIsEdited(true);
											value && setPrompt((cur) => ({ ...cur, prompt: value.Arn }));
										}}
										value={connectPromptsList.find((e) => e.Arn === prompt.prompt)}
										freeSolo
										getOptionLabel={(option) => option.Name}
										fullWidth
										renderInput={(params) => (
											<TextField
												{...params}
												id="promptTextField"
												label={getPromptFieldLabel(prompt.type)}
												margin="dense"
												error={!!getError("prompt")}
												helperText={getError("prompt")}
												required
												size="small"
												value={prompt.prompt || ""}
											/>
										)}
									/>
								) : (
									<FormTextField
										label={getPromptFieldLabel(prompt.type)}
										formEdited={isEdited}
										setEdited={setIsEdited}
										errorMessage={getError("prompt")}
										startingValue={prompt.prompt || ""}
										setConfigValue={updateProperty}
										valueName={"prompt"}
										styleObject={{ width: "100%" }}
										enableDebounce
										errorCheck={() => !!getError("prompt")}
										placeholder=""
										toolTipMessage={PROMPT_FIELD}
										multiline
										maxLineRows={3}
									/>
								)}
							</Grid>
						</Grid>
					</DialogContent>
					<DialogActions>
						<ActionButtons
							showCancel
							className="d-flex mx-3 mt-3"
							onCancel={onPromptClose}
							showSave
							disableSave={!validation.isValid || isSaving}
							onSave={isNew ? onCreatePrompt : onUpdatePrompt}
							saveLabel={isNew ? "Create Prompt" : "Save Prompt"}
						/>
					</DialogActions>
				</Dialog>
			)}
		</div>
	);
};

export default PromptEditForm;
